<template>
  <div class="create-campaign-email-body-editor" :class="{ 'create-campaign-email-body-editor--focused': isFocused }">
    <div class="create-campaign-email-body-editor__toolbar">
      <common-tooltip :max-width="null" :min-width="0" :z-index="8">
        <template #activator>
          <button
            type="button"
            :class="{ 'create-campaign-email-body-editor__toolbar-button--active': editor && editor.isActive('bold') }"
            @mousedown.prevent
            @click="editor.chain().focus().toggleBold().run()"
          >
            <strong>B</strong>
          </button>
        </template>

        Bold
      </common-tooltip>

      <common-tooltip :max-width="null" :min-width="0" :z-index="8">
        <template #activator>
          <button
            type="button"
            :class="{ 'create-campaign-email-body-editor__toolbar-button--active': editor && editor.isActive('italic') }"
            @mousedown.prevent
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <em>I</em>
          </button>
        </template>

        Italic
      </common-tooltip>

      <common-tooltip :max-width="null" :min-width="0" :z-index="8">
        <template #activator>
          <button
            type="button"
            :class="{ 'create-campaign-email-body-editor__toolbar-button--active': editor && editor.isActive('underline') }"
            @mousedown.prevent
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <u>U</u>
          </button>
        </template>

        Underline
      </common-tooltip>

      <common-tooltip :max-width="null" :min-width="0" :z-index="8">
        <template #activator>
          <button
            type="button"
            @mousedown.prevent
            @click="editor.chain().focus().setHardBreak().run()"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 10 4 15 9 20" />
              <path d="M20 4v7a4 4 0 0 1-4 4H4" />
            </svg>
          </button>
        </template>

        Insert line break
      </common-tooltip>
    </div>

    <editor-content :editor="editor" class="create-campaign-email-body-editor__content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import { markInputRule } from '@tiptap/core'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import HardBreak from '@tiptap/extension-hard-break'

const BoldWithHtmlShortcut = Bold.extend({
  addInputRules () {
    return [
      ...this.parent?.() ?? [],
      markInputRule({ find: /<(?:b|strong)>([^<]+)<\/(?:b|strong)>$/i, type: this.type }),
    ]
  },
})

const ItalicWithHtmlShortcut = Italic.extend({
  addInputRules () {
    return [
      ...this.parent?.() ?? [],
      markInputRule({ find: /<(?:i|em)>([^<]+)<\/(?:i|em)>$/i, type: this.type }),
    ]
  },
})

const UnderlineWithHtmlShortcut = Underline.extend({
  addInputRules () {
    return [
      ...this.parent?.() ?? [],
      markInputRule({ find: /<u>([^<]+)<\/u>$/i, type: this.type }),
    ]
  },
})

export default {
  name: 'CreateCampaignEmailBodyEditor',
  components: { EditorContent },
  props: {
    value: {
      type: String,
      required: false,
      default: null,
    },
  },
  data: () => ({
    editor: null,
    isFocused: false,
  }),
  watch: {
    value (val) {
      if (!this.editor || this.editor.getHTML() === (val || '<p></p>')) { return }
      this.editor.commands.setContent(val || '', false)
    },
  },
  mounted () {
    this.editor = new Editor({
      content: this.value || '',
      extensions: [
        Document,
        Paragraph,
        Text,
        BoldWithHtmlShortcut,
        ItalicWithHtmlShortcut,
        UnderlineWithHtmlShortcut,
        HardBreak.extend({
          addKeyboardShortcuts () {
            return { Enter: () => this.editor.commands.setHardBreak() }
          },
        }),
      ],
      onUpdate: () => {
        this.$emit('input', this.editor.getHTML())
      },
      onFocus: () => { this.isFocused = true },
      onBlur: () => { this.isFocused = false },
    })
  },
  beforeDestroy () {
    this.editor?.destroy()
  },
}
</script>

<style lang="scss" scoped>
.create-campaign-email-body-editor {
  position: relative;
  border: 1px solid #D3D2D2;

  &--focused {
    border-color: #A7DAD8;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid #A7DAD8;
      pointer-events: none;
    }
  }

  &__toolbar {
    display: flex;
    flex-direction: row;
    gap: 4px;
    padding: 6px;
    background-color: #FAFAFA;
    border-bottom: 1px solid #D3D2D2;

    button {
      width: 28px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      color: #62646A;
      font-size: 13px;

      &:hover {
        background-color: #EDFCFB;
      }
    }
  }

  &__toolbar-button--active {
    background-color: #EDFCFB;
    color: #6E50FF;
  }

  &__content {
    padding: 8px 12px;

    ::v-deep .ProseMirror {
      min-height: 80px;
      outline: none;
      font-family: 'Lato', sans-serif;
      font-size: 15px;
      line-height: 18px;
      color: #222325;
    }
  }
}
</style>
